import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';

import { API_URL } from '../../config/api';

const PasswordResetRequest = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username && !formData.email) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please provide either username or email'
      }));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/password-reset/request`, formData);
      
      setSuccess(true);
      setFormData({ username: '', email: '', reason: '' });
      
      dispatch(addNotification({
        type: 'success',
        message: 'Password reset request submitted successfully. Please wait for admin approval.'
      }));
    } catch (error) {
      console.error('Failed to request password reset:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to submit password reset request'
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Request Password Reset
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Your request will be reviewed by an administrator
          </p>
        </div>

        {success ? (
          <div className="rounded-lg bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-green-800 dark:text-green-200 mb-2">
                  Request Submitted Successfully
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                  Your password reset request has been submitted and is pending admin approval. 
                  Once approved, you will be able to set a new password.
                </p>
                <Link
                  to="/login"
                  className="text-sm font-medium text-green-800 dark:text-green-200 hover:underline"
                >
                  Back to Login
                </Link>
                <p className="mt-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Link
                    to="/privacy"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-green-600 dark:text-green-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/terms"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Terms
                  </Link>
                  <span className="text-green-600 dark:text-green-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/support"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Support
                  </Link>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username (optional if email provided)
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email (optional if username provided)
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Optional: Explain why you need to reset your password"
                  value={formData.reason}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>

            <div className="text-center space-y-2">
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 block"
              >
                Back to Login
              </Link>
              <p className="text-sm flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <Link
                  to="/privacy"
                  className="font-medium text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                >
                  Privacy Policy
                </Link>
                <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                  ·
                </span>
                <Link
                  to="/terms"
                  className="font-medium text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                >
                  Terms
                </Link>
                <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                  ·
                </span>
                <Link
                  to="/support"
                  className="font-medium text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                >
                  Support
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PasswordResetRequest;

